import * as R from 'ramda';
import * as Yup from 'yup';
// helpers/constants
import * as G from '../../../helpers';
import * as GC from '../../../constants';
import { getFreightClassOptions } from '../../../helpers/options';
//////////////////////////////////////////////////

const inputWrapperStyles = {
  mb: 25,
  width: 280,
};

const noFreightClass = 'NO_FREIGHT_CLASS';

const fieldsWrapperStyles = {
  flexDirection: 'column',
};

const fields = {
  [GC.FIELD_RATE]: {
    width: 100,
    type: 'number',
    isRequired: true,
    name: 'titles:rate',
  },
  [GC.FIELD_CHARGE_RATE_UNIT]: {
    width: 150,
    type: 'select',
    isRequired: true,
    name: 'titles:rate-unit',
    options: [
      GC.EMPTY_OPTION_OBJECT,
      { value: GC.DISCOUNT_TYPE_FLAT, label: G.getWindowLocale('titles:flat', 'Flat') },
      { value: GC.CHARGE_RATE_UNIT_TYPE_PERCENT, label: G.getWindowLocale('titles:percent', 'Percent') },
    ],
  },
  [GC.FIELD_VALUE_FROM]: {
    width: 110,
    type: 'number',
    isRequired: true,
    name: 'titles:value-from',
  },
  [GC.FIELD_VALUE_TO]: {
    width: 110,
    type: 'number',
    isRequired: true,
    name: 'titles:value-to',
  },
};

const smc3VariableDiscountFields = R.assocPath(
  [GC.FIELD_CHARGE_RATE_UNIT, 'options'],
  [
    GC.EMPTY_OPTION_OBJECT,
    { value: GC.CHARGE_RATE_UNIT_TYPE_PERCENT, label: G.getWindowLocale('titles:percent', 'Percent') },
  ],
  fields,
);

const validationSchema = Yup.object().shape({
  [GC.FIELD_CHARGE_RATE_UNIT]: G.yupStringRequired,
  [GC.FIELD_RATE]: G.yupNumberRequired.max(9999999, G.getShouldBeLessOrEqualLocaleTxt(9999999)),
  [GC.FIELD_VALUE_TO]: G.yupNumberRequired.max(9999999, G.getShouldBeLessOrEqualLocaleTxt(9999999)),
  [GC.FIELD_VALUE_FROM]: G.yupNumberRequired.max(9999999, G.getShouldBeLessOrEqualLocaleTxt(9999999)),
});

const smc3ValidationSchema = validationSchema.shape({
  [GC.FIELD_CHARGE_RATE_UNIT]: Yup.string()
    .nullable(true)
    .required(G.getRequiredLocaleTxt())
    .oneOf([GC.CHARGE_RATE_UNIT_TYPE_PERCENT], G.getFieldIsNotValidLocaleTxt()),
});

const variableValuesSettings = {
  fields,
  endpoints: {},
  validationSchema,
  inputWrapperStyles,
  fieldsWrapperStyles,
  tableInnerWidth: 940,
  groupName: 'variableDiscounts',
  itemTitleArr: ['titles:discount', 'Discount'],
  omitColumns: GC.GROUPED_FIELDS.SYSTEM_OMIT_ARR_2,
  formGroupTitleArr: ['titles:variable-discounts', 'Variable Discounts'],
  // helpers
  customRemoveHandler: ({ setValues, closeModal }, { guid }) => setValues((prevValues) => {
    const variableValues = R.reject(
      R.propEq(guid, GC.FIELD_GUID),
      R.path([GC.CHARGE_TYPE_DISCOUNT, GC.FIELD_PAYROLL_VARIABLE_VALUES], prevValues),
    );

    if (G.isZero(R.length(variableValues))) {
      G.showToastrMessageSimple('error', 'At least one of range values is required');

      return prevValues;
    }

    closeModal();

    return R.assocPath([GC.CHARGE_TYPE_DISCOUNT, GC.FIELD_PAYROLL_VARIABLE_VALUES], variableValues, prevValues);
  }),
  customCreateOrUpdateHandler: ({ values, setValues, closeModal }) => setValues((prevValues) => {
    const { guid } = values;

    const variableValues = R.pathOr([], [GC.CHARGE_TYPE_DISCOUNT, GC.FIELD_PAYROLL_VARIABLE_VALUES], prevValues);

    closeModal();

    if (R.isNotNil(guid)) {
      const index = R.findIndex(R.propEq(guid, GC.FIELD_GUID), variableValues);

      return R.assocPath([GC.CHARGE_TYPE_DISCOUNT, GC.FIELD_PAYROLL_VARIABLE_VALUES, index], values, prevValues);
    }

    const item = G.ifElse(
      R.isNil(G.getGuidFromObject(values)),
      R.assoc(GC.FIELD_GUID, G.generateGuid(), values),
      values,
    );

    return R.assocPath(
      [GC.CHARGE_TYPE_DISCOUNT, GC.FIELD_PAYROLL_VARIABLE_VALUES],
      R.append(item, variableValues),
      prevValues,
    );
  }),
};

const smc3VariableValuesSettings = {
  ...variableValuesSettings,
  fields: smc3VariableDiscountFields,
  groupName: 'smc3VariableDiscounts',
  validationSchema: smc3ValidationSchema,
};

const awardedLaneVariableDiscountSettings = {
  ...variableValuesSettings,
  tableInnerWidth: 980,
  groupName: 'awardedLaneVariableDiscounts',
};

const smc3AwardedLaneVariableDiscountSettings = {
  ...smc3VariableValuesSettings,
  tableInnerWidth: 980,
  groupName: 'smc3AwardedLaneVariableDiscounts',
};

const customerContractVariableDiscountSettings = {
  ...variableValuesSettings,
  groupName: 'customerContractVariableDiscounts',
};

const getFreightClassOverrideOptions = () => R.reject(
  ({ value }) => R.includes(value, ['', noFreightClass]),
  getFreightClassOptions(),
);

const getFreightClassOrder = (value) => R.compose(
  parseFloat,
  R.propOr('', 'label'),
  R.find(R.propEq(value, GC.FIELD_VALUE)),
)(getFreightClassOverrideOptions());

const getFreightClassLabel = (value) => R.compose(
  R.propOr(value, 'label'),
  R.find(R.propEq(value, GC.FIELD_VALUE)),
)(getFreightClassOverrideOptions());

const hasFreightClassOverrideOverlap = (overrides = []) => {
  const ranges = R.compose(
    R.sortBy(R.prop('from')),
    R.map((item) => ({
      guid: R.prop(GC.FIELD_GUID, item),
      from: getFreightClassOrder(R.prop(GC.FIELD_FROM_CLASS, item)),
      to: getFreightClassOrder(R.prop(GC.FIELD_TO_CLASS, item)),
    })),
    R.filter((item) => R.allPass([
      R.compose(Number.isFinite, getFreightClassOrder, R.prop(GC.FIELD_FROM_CLASS)),
      R.compose(Number.isFinite, getFreightClassOrder, R.prop(GC.FIELD_TO_CLASS)),
    ])(item)),
  )(overrides);

  return R.any(
    (index) => R.lt(
      R.path([index, 'from'], ranges),
      R.path([R.dec(index), 'to'], ranges),
    ),
    R.range(1, R.length(ranges)),
  );
};

const freightClassOverrideRowValidationSchema = Yup.object().shape({
  [GC.FIELD_TO_CLASS]: G.yupStringRequired,
  [GC.FIELD_OVERRIDE_CLASS]: G.yupStringRequired,
  [GC.FIELD_FROM_CLASS]: G.yupStringRequired.test(
    'from-class-lower-than-to-class',
    G.getWindowLocale('messages:from-class-must-be-lower-than-to-class', 'From class must be lower than to class'),
    function validateFromClass(value) {
      const { toClass } = this.parent;

      const fromOrder = getFreightClassOrder(value);
      const toOrder = getFreightClassOrder(toClass);

      if (R.anyPass([R.isNil, Number.isNaN])(fromOrder)) return true;

      if (R.anyPass([R.isNil, Number.isNaN])(toOrder)) return true;

      return R.lt(fromOrder, toOrder);
    },
  ),
});

const freightClassOverridesOverlapMessage = G.getWindowLocale(
  'messages:freight-class-overrides-must-not-overlap',
  'Freight class override ranges must not overlap',
);

const freightClassOverridesValidationSchema = Yup.array()
  .nullable(true)
  .notRequired()
  .of(freightClassOverrideRowValidationSchema)
  .test(
    'freight-class-overrides-no-overlap',
    freightClassOverridesOverlapMessage,
    (overrides) => R.not(hasFreightClassOverrideOverlap(R.or(overrides, []))),
  );

const freightClassOverrideFields = {
  [GC.FIELD_FROM_CLASS]: {
    width: 150,
    isRequired: true,
    type: 'reactSelect',
    name: 'titles:from-class',
    options: 'freightClassOverrideOptions',
    customComponent: ({ data }) => getFreightClassLabel(R.prop(GC.FIELD_FROM_CLASS, data)),
  },
  [GC.FIELD_TO_CLASS]: {
    width: 150,
    isRequired: true,
    type: 'reactSelect',
    name: 'titles:to-class',
    options: 'freightClassOverrideOptions',
    customComponent: ({ data }) => getFreightClassLabel(R.prop(GC.FIELD_TO_CLASS, data)),
  },
  [GC.FIELD_OVERRIDE_CLASS]: {
    width: 150,
    isRequired: true,
    type: 'reactSelect',
    name: 'titles:override-class',
    options: 'freightClassOverrideOptions',
    customComponent: ({ data }) => getFreightClassLabel(R.prop(GC.FIELD_OVERRIDE_CLASS, data)),
  },
};

const createFreightClassOverrideSettings = ({ arrayPath, groupName, tableInnerWidth }) => ({
  groupName,
  endpoints: {},
  tableInnerWidth,
  inputWrapperStyles,
  fieldsWrapperStyles,
  fields: freightClassOverrideFields,
  omitColumns: GC.GROUPED_FIELDS.SYSTEM_OMIT_ARR_2,
  validationSchema: freightClassOverrideRowValidationSchema,
  itemTitleArr: ['titles:freight-class-override', 'Freight Class Override'],
  formGroupTitleArr: ['titles:freight-class-overrides', 'Freight Class Overrides'],
  makeOptionsForSelect: () => ({ freightClassOverrideOptions: getFreightClassOverrideOptions() }),
  customRemoveHandler: ({ setValues, closeModal }, { guid }) => setValues((prevValues) => {
    const overrides = R.reject(
      R.propEq(guid, GC.FIELD_GUID),
      R.pathOr([], arrayPath, prevValues),
    );

    closeModal();

    return R.assocPath(arrayPath, overrides, prevValues);
  }),
  customCreateOrUpdateHandler: ({ values, setValues, closeModal }, options) => setValues((prevValues) => {
    const { guid } = values;

    const overrides = R.pathOr([], arrayPath, prevValues);

    const item = G.ifElse(
      R.isNil(G.getGuidFromObject(values)),
      R.assoc(GC.FIELD_GUID, G.generateGuid(), values),
      values,
    );

    const nextOverrides = G.ifElse(
      R.isNotNil(guid),
      R.update(R.findIndex(R.propEq(guid, GC.FIELD_GUID), overrides), values, overrides),
      R.append(item, overrides),
    );

    if (hasFreightClassOverrideOverlap(nextOverrides)) {
      G.callFunctionWithArgs(options.failCallback, false);

      G.showToastrMessageSimple(
        'error',
        freightClassOverridesOverlapMessage,
      );

      return prevValues;
    }

    closeModal();

    return R.assocPath(arrayPath, nextOverrides, prevValues);
  }),
});

const freightClassOverrideSettings = createFreightClassOverrideSettings({
  tableInnerWidth: 940,
  groupName: 'freightClassOverrides',
  arrayPath: [GC.FIELD_FREIGHT_CLASS_OVERRIDES],
});

const awardedLaneFreightClassOverrideSettings = createFreightClassOverrideSettings({
  tableInnerWidth: 980,
  groupName: 'awardedLaneFreightClassOverrides',
  arrayPath: [GC.FIELD_FREIGHT_CLASS_OVERRIDES],
});

export {
  variableValuesSettings,
  smc3VariableValuesSettings,
  freightClassOverrideSettings,
  awardedLaneVariableDiscountSettings,
  freightClassOverridesValidationSchema,
  awardedLaneFreightClassOverrideSettings,
  smc3AwardedLaneVariableDiscountSettings,
  customerContractVariableDiscountSettings,
};
